// src/sectors/pilates/attendance/ui/AttendanceMarkModal/RenewPackageModal.jsx
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

import { PackageCreatePanel } from 'sectors/pilates/members/modal/PackageCreatePanel';
import { PlanBuilder } from 'sectors/pilates/members/modal/MemberCreateModal/parts/plan/PlanBuilder';
import { membersApi } from 'sectors/pilates/members/api/membersApi';

// ---------- helpers ----------
const MONTHS_TR = [
  'Ocak',
  'Şubat',
  'Mart',
  'Nisan',
  'Mayıs',
  'Haziran',
  'Temmuz',
  'Ağustos',
  'Eylül',
  'Ekim',
  'Kasım',
  'Aralık',
];

function parseLocalDateString(s) {
  if (!s || typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const da = Number(m[3]);
  const dt = new Date(y, mo - 1, da);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function dMonYYYY_TR(dateLike) {
  const d = dateLike instanceof Date ? dateLike : parseLocalDateString(dateLike);
  if (!d) return '—';
  return `${d.getDate()} ${MONTHS_TR[d.getMonth()]} ${d.getFullYear()}`;
}

function moneyTRY(n) {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('tr-TR').format(n) + ' ₺';
}

function resolveMemberId(member) {
  return member?.id ?? member?._id ?? member?.memberId ?? null;
}

function resolvePlanId(p) {
  return p?.id ?? p?._id ?? p?.memberPlanId ?? p?.memberPackageId ?? p?.packageId ?? null;
}

function planName(p) {
  return p?.planName ?? p?.planSnapshot?.name ?? p?.name ?? 'Paket';
}

// ---------- component ----------
export function RenewPackageModal({
  open,
  onClose,
  member,
  participant,
  api,
  activities = [],
  startAt,
  onDone,
}) {
  const theme = useMuiTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const memberId = resolveMemberId(member);

  const [mode, setMode] = useState('EXISTING'); // EXISTING | PICK | CUSTOM

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [plans, setPlans] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [busy, setBusy] = useState(false);

  // custom builder
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderBusy, setBuilderBusy] = useState(false);
  const [builderErr, setBuilderErr] = useState('');

  const openCustom = () => {
    setBuilderErr('');
    setBuilderOpen(true);
  };

  const closeBuilder = () => {
    if (builderBusy) return;
    setBuilderOpen(false);
  };

  useEffect(() => {
    if (!open) return;

    setErr('');
    setPlans([]);
    setSelectedId(null);
    setBusy(false);

    setBuilderOpen(false);
    setBuilderBusy(false);
    setBuilderErr('');

    setMode('EXISTING');

    if (!memberId) {
      setErr('Üye bulunamadı.');
      return;
    }

    let alive = true;
    setLoading(true);

    Promise.resolve(api?.listMemberPlans?.(memberId))
      .then((res) => {
        if (!alive) return;

        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.items)
          ? res.items
          : Array.isArray(res?.content)
          ? res.content
          : Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.plans)
          ? res.plans
          : [];

        setPlans(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (!alive) return;
        setErr(e?.response?.data?.message || e?.message || 'Paketler getirilemedi.');
        setPlans([]);
      })
      .finally(() => alive && setLoading(false));

    return () => {
      alive = false;
    };
  }, [open, memberId, api]);

  const rows = useMemo(() => {
    return (plans || []).map((p) => {
      const id = resolvePlanId(p);
      const name = planName(p);

      const sAt = p?.startAt ?? p?.plannedStartAt ?? p?.startDate ?? null;
      const eAt = p?.endAt ?? p?.estimatedEndAt ?? p?.endDate ?? null;

      const total =
        typeof p?.totalTry === 'number'
          ? p.totalTry
          : typeof p?.agreedPriceTry === 'number'
          ? p.agreedPriceTry
          : typeof p?.planPriceTry === 'number'
          ? p.planPriceTry
          : null;

      const used = p?.usedSessions ?? p?.usage?.usedSessions ?? null;
      const cap = p?.totalSessions ?? p?.usage?.totalSessions ?? null;

      return {
        raw: p,
        id,
        name,
        startAtLabel: dMonYYYY_TR(sAt),
        endAtLabel: dMonYYYY_TR(eAt),
        priceLabel: total != null ? moneyTRY(total) : '—',
        usageLabel: cap != null && used != null ? `${used}/${cap} seans` : null,
      };
    });
  }, [plans]);

  const selectedRow = rows.find((r) => String(r.id) === String(selectedId));
  const canRenew = !!memberId && !!selectedRow && !busy && !loading;

  const doRenew = async () => {
    if (!canRenew) return;
    setBusy(true);
    setErr('');
    try {
      const res = await api?.renewFromPlan?.(memberId, selectedRow.id, startAt);

      const newId = res?.newMemberPlanId ?? res?.memberPlanId ?? res?.id ?? res?._id ?? null;
      if (!newId) throw new Error('Yeni paket id alınamadı.');

      onDone?.({ memberPlanId: newId, mode: 'RENEW_FROM_EXISTING' });
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.message || e?.message || 'Paket yenilenemedi.');
    } finally {
      setBusy(false);
    }
  };

  const doCreateCustom = async (payload) => {
    if (!memberId) return;
    setBuilderBusy(true);
    setBuilderErr('');
    try {
      const res = await api?.createCustomPlanForMember?.(memberId, payload);
      const newId = res?.memberPlanId ?? res?.id ?? res?._id ?? res?.createdMemberPlanId ?? null;

      if (!newId) throw new Error('Özel paket id alınamadı.');

      setBuilderOpen(false);
      onDone?.({ memberPlanId: newId, mode: 'CUSTOM', payload });
      onClose?.();
    } catch (e) {
      setBuilderErr(e?.response?.data?.message || e?.message || 'Özel paket oluşturulamadı.');
    } finally {
      setBuilderBusy(false);
    }
  };

  const safeClose = () => {
    if (busy || builderBusy) return;
    onClose?.();
  };

  return (
    <>
      <Dialog
        open={!!open}
        onClose={safeClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '20px',
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.info.main, 0.14)}`,
            background: alpha(
              theme.palette.background.paper,
              theme.palette.mode === 'dark' ? 0.92 : 0.98
            ),
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <MDBox display="flex" alignItems="center" gap={1}>
              <MDBox
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '14px',
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: alpha(theme.palette.warning.main, 0.14),
                  border: `1px solid ${alpha(theme.palette.warning.main, 0.22)}`,
                }}
              >
                <Icon sx={{ fontSize: 20, color: theme.palette.warning.main }}>redeem</Icon>
              </MDBox>

              <MDBox sx={{ minWidth: 0 }}>
                <MDTypography fontWeight="bold" sx={{ lineHeight: 1.1 }} noWrap>
                  Paket İşlemleri
                </MDTypography>
                <MDTypography variant="caption" sx={{ opacity: 0.82 }} noWrap>
                  {participant?.memberFullName ? `${participant.memberFullName} • ` : ''}
                  Yenile • Plan seç • Özel paket
                </MDTypography>
              </MDBox>
            </MDBox>

            <IconButton onClick={safeClose} disabled={busy || builderBusy}>
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 1.2 }}>
          {!memberId ? (
            <MDBox sx={{ p: 1.2 }}>
              <MDTypography fontWeight="bold">Üye bulunamadı</MDTypography>
              <MDTypography variant="caption" sx={{ opacity: 0.85 }}>
                memberId resolve edilemedi.
              </MDTypography>
            </MDBox>
          ) : null}

          {err ? (
            <MDBox
              sx={{
                mb: 1,
                p: 1.1,
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
                bgcolor: alpha(theme.palette.error.main, 0.08),
              }}
            >
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{ color: theme.palette.error.main }}
              >
                Hata
              </MDTypography>
              <MDTypography variant="caption" sx={{ display: 'block', mt: 0.35, opacity: 0.9 }}>
                {err}
              </MDTypography>
            </MDBox>
          ) : null}

          <MDBox sx={{ mb: 1.2, display: 'grid', gap: 1 }}>
            <ToggleButtonGroup
              value={mode}
              exclusive
              onChange={(_, v) => {
                if (!v) return;
                setMode(v);
                if (v === 'CUSTOM') openCustom();
              }}
              size="small"
              sx={{
                width: 'fit-content',
                borderRadius: '14px',
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                '& .MuiToggleButton-root': {
                  border: 'none',
                  px: 1.4,
                  py: 0.8,
                  fontWeight: 900,
                  textTransform: 'none',
                },
              }}
            >
              <ToggleButton value="EXISTING">Eski Paketten</ToggleButton>
              <ToggleButton value="PICK">Plan Seç</ToggleButton>
              <ToggleButton value="CUSTOM">Özel Paket</ToggleButton>
            </ToggleButtonGroup>

            {mode === 'EXISTING' ? (
              <MDBox
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
              >
                <MDTypography variant="button" fontWeight="bold">
                  Eski Paketler
                </MDTypography>

                {loading ? (
                  <MDBox sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <CircularProgress size={18} />
                    <MDTypography variant="caption" sx={{ opacity: 0.8, fontWeight: 700 }}>
                      Yükleniyor…
                    </MDTypography>
                  </MDBox>
                ) : (
                  <Chip
                    size="small"
                    label={`${rows.length} paket`}
                    sx={{
                      height: 24,
                      borderRadius: '999px',
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
                      fontWeight: 900,
                    }}
                  />
                )}
              </MDBox>
            ) : null}
          </MDBox>

          {/* PICK: Şablondan plan seçip yeni paket oluştur */}
          {mode === 'PICK' ? (
            <PackageCreatePanel
              open={open}
              memberId={memberId}
              startAt={startAt}
              api={{ createMemberPackage: membersApi.createMemberPackage }}
              onCancel={() => setMode('EXISTING')}
              onCreated={(res, dto) => {
                const newId =
                  res?.memberPlanId ?? res?.id ?? res?._id ?? res?.createdMemberPlanId ?? null;

                onDone?.({ memberPlanId: newId, mode: 'PICK_FROM_LIBRARY', payload: dto });
                onClose?.();
              }}
            />
          ) : null}

          {/* EXISTING: eski paketten yenile */}
          {mode === 'EXISTING' ? (
            <>
              <List sx={{ p: 0, display: 'grid', gap: 1 }}>
                {rows.map((r) => {
                  const selected = String(r.id) === String(selectedId);

                  return (
                    <ListItemButton
                      key={String(r.id)}
                      onClick={() => setSelectedId(r.id)}
                      sx={{
                        borderRadius: '16px',
                        border: `1px solid ${
                          selected
                            ? alpha(theme.palette.warning.main, 0.55)
                            : alpha(theme.palette.divider, 0.9)
                        }`,
                        background: selected
                          ? alpha(theme.palette.warning.main, 0.1)
                          : 'transparent',
                        transition: 'all .14s ease',
                        '&:hover': {
                          borderColor: alpha(theme.palette.warning.main, 0.45),
                          background: alpha(theme.palette.warning.main, 0.07),
                        },
                      }}
                    >
                      <Icon
                        sx={{
                          mr: 1,
                          color: selected
                            ? theme.palette.warning.main
                            : theme.palette.text.secondary,
                        }}
                      >
                        {selected ? 'check_circle' : 'radio_button_unchecked'}
                      </Icon>

                      <ListItemText
                        primary={
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={1}
                          >
                            <MDBox sx={{ minWidth: 0 }}>
                              <MDTypography fontWeight="bold" noWrap>
                                {r.name}
                              </MDTypography>
                              <MDTypography variant="caption" sx={{ opacity: 0.82 }}>
                                {r.startAtLabel} — {r.endAtLabel}
                                {r.usageLabel ? ` • ${r.usageLabel}` : ''}
                              </MDTypography>
                            </MDBox>

                            <Chip
                              size="small"
                              label={r.priceLabel}
                              sx={{
                                height: 24,
                                borderRadius: '999px',
                                fontWeight: 900,
                                bgcolor: alpha(theme.palette.success.main, 0.1),
                                border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                              }}
                            />
                          </MDBox>
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>

              <MDBox
                sx={{
                  mt: 1.2,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={() => {
                    setMode('CUSTOM');
                    openCustom();
                  }}
                  sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900 }}
                  disabled={!memberId || busy}
                >
                  <Icon sx={{ mr: 0.6, fontSize: 18 }}>auto_fix_high</Icon>
                  Özel Paket
                </MDButton>

                <MDTypography variant="caption" sx={{ opacity: 0.75, alignSelf: 'center' }}>
                  İstersen eski paketlerden birini yenileyebilirsin.
                </MDTypography>
              </MDBox>
            </>
          ) : null}

          {/* CUSTOM: builder ayrı dialog açıldığı için burada ekstra içerik göstermiyoruz */}
        </DialogContent>

        <Divider />

        {/* Footer sadece EXISTING’te: yenileme butonu */}
        {mode === 'EXISTING' ? (
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <MDButton
              variant="outlined"
              color="info"
              onClick={safeClose}
              sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900, flex: 1 }}
              disabled={busy || builderBusy}
            >
              Vazgeç
            </MDButton>

            <MDButton
              variant="gradient"
              color="warning"
              onClick={doRenew}
              disabled={!canRenew}
              sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900, flex: 1 }}
            >
              {busy ? 'Yenileniyor...' : 'Seçili Paketi Yenile'}
            </MDButton>
          </DialogActions>
        ) : null}
      </Dialog>

      {/* Custom Plan Builder */}
      <Dialog
        open={builderOpen}
        onClose={closeBuilder}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            border: `1px solid ${theme.palette.divider}`,
            background:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(180deg, rgba(26,28,36,.92), rgba(18,20,26,.96))'
                : 'linear-gradient(180deg, rgba(255,255,255,.96), rgba(250,250,252,.98))',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDBox display="flex" alignItems="center" gap={1}>
              <Icon sx={{ fontSize: 20, color: theme.palette.warning.main }}>construction</Icon>
              <MDTypography fontWeight="bold">Özel Paket Oluştur</MDTypography>
            </MDBox>
            <IconButton size="small" sx={{ p: 0.6, borderRadius: '12px' }} onClick={closeBuilder}>
              <Icon sx={{ color: theme.palette.text.secondary }}>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>

        <DialogContent sx={{ pt: 0, pb: 2 }}>
          {builderErr ? (
            <MDBox
              sx={{
                mb: 1,
                p: 1.1,
                borderRadius: '16px',
                border: `1px solid ${alpha(theme.palette.error.main, 0.22)}`,
                bgcolor: alpha(theme.palette.error.main, 0.08),
              }}
            >
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{ color: theme.palette.error.main }}
              >
                Hata
              </MDTypography>
              <MDTypography variant="caption" sx={{ display: 'block', mt: 0.35, opacity: 0.9 }}>
                {builderErr}
              </MDTypography>
            </MDBox>
          ) : null}

          <PlanBuilder
            mode="CUSTOM"
            initialPlan={null}
            activities={activities}
            onSave={async (payload) => {
              return { payload };
            }}
            onDone={async (res) => {
              const payload = res?.payload || res?.plan || res?.data || null;
              if (!payload) {
                setBuilderErr('Özel plan payload alınamadı.');
                return;
              }

              setBuilderBusy(true);
              setBuilderErr('');
              try {
                await doCreateCustom({
                  ...payload,
                  priceTry: payload.priceTry ?? 0,
                  classCapacity: payload.classCapacity ?? 1,
                });
              } finally {
                setBuilderBusy(false);
              }
            }}
            onCloseBuilder={closeBuilder}
          />
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={closeBuilder}
            sx={{ borderRadius: '14px', textTransform: 'none', fontWeight: 900 }}
            disabled={builderBusy}
          >
            Kapat
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

RenewPackageModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  member: PropTypes.object,
  participant: PropTypes.object,
  api: PropTypes.shape({
    listMemberPlans: PropTypes.func.isRequired,
    renewFromPlan: PropTypes.func.isRequired,
    createCustomPlanForMember: PropTypes.func.isRequired,
    createMemberPackage: PropTypes.func.isRequired,
  }).isRequired,
  activities: PropTypes.array,
  startAt: PropTypes.string,
  onDone: PropTypes.func,
};
